import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { Formik } from "formik";

import {
  Alert as MuiAlert,
  Button,
  TextField as MuiTextField,
} from "@mui/material";
import { spacing } from "@mui/system";

import useAuth from "../../hooks/useAuth";
import { trackLoginSuccess } from "../../analytics/signupTracking";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const UserRoot = styled.div`
  max-width: 540px;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

function SignIn() {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  return (
    <UserRoot>
      <Formik
        initialValues={{
          email: "",
          password: "",
          submit: false,
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Sähköpostin on oltava kelvollinen")
            .max(255)
            .required("Sähköposti on pakollinen"),
          password: Yup.string().max(255).required("Salasana on pakollinen"),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            await signIn(values.email, values.password);

            // Track successful login
            trackLoginSuccess();

            navigate("/");
          } catch (error: any) {
            let message =
              "Jotain meni pieleen. Ota yhteys asiakaspalvelu@tiehoitokunta.fi";

            // Check if the error response has the message
            if (error.response?.data?.message) {
              message = error.response.data.message;
            } else if (error.response?.status === 401) {
              message = "Väärä käyttäjätunnus tai salasana";
            }

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert mt={2} mb={3} severity="warning">
                {errors.submit}
              </Alert>
            )}
            <TextField
              type="email"
              name="email"
              label="Sähköposti"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />
            <TextField
              type="password"
              name="password"
              label="Salasana"
              value={values.password}
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />
            {/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Muista minut"
            /> */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ my: 2 }}
            >
              Kirjaudu
            </Button>
            <Button
              component={Link}
              to="/reset-password"
              fullWidth
              color="primary"
            >
              Unohtunut salasana
            </Button>
            <div style={{ display: "flex", justifyContent: "center" }}>
              Eikö sinulla ole tiliä?
              <span
                style={{
                  fontWeight: "bold",
                  marginLeft: "5px",
                }}
              >
                <a
                  style={{ color: "rgb(55, 111, 208)", textDecoration: "none" }}
                  href="/register"
                >
                  Rekisteröidy
                </a>
              </span>
            </div>
          </form>
        )}
      </Formik>
    </UserRoot>
  );
}

export default SignIn;
